// File: Frontend/src/store/posts/posts.ts

import { type Post, PostStatus } from '@creator/sdk/modules/upvote/upvote.model';
import { getFlairNameById } from '@src/model/token/helpers';
import creatorSdk from '@src/services/creator-sdk';
import {
    type CollectionReference,
    type DocumentReference,
    type DocumentSnapshot,
    type QueryConstraint,
    type QueryDocumentSnapshot,
    collection,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    orderBy,
    query,
    startAfter,
    where,
} from 'firebase/firestore/lite';
import { atom } from 'jotai';
import { atomWithImmer } from 'jotai-immer';
import { atomFamily, loadable } from 'jotai/utils';
import { jotaiStore } from '../store';

// --- Core Types ---

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';
export type PostCollectionType = 'partner' | 'pinned' | 'user' | 'community' | 'recommended' | 'pending' | 'deleted' | 'scheduled' | 'draft';

// --- Base Post Data Store ---

/** Atom holding the normalized cache of all loaded posts */
export const postsAtom = atomWithImmer<Record<string, Post>>({});
postsAtom.debugLabel = 'postsAtom';

/** Imperatively update or add a single post to the cache */
export function setPost(post: Post) {
    jotaiStore.set(postsAtom, draft => {
        draft[post.id] = post;
    });
}

/** Imperatively update or add multiple posts to the cache */
export function setPosts(posts: Record<string, Post>) {
    jotaiStore.set(postsAtom, draft => {
        Object.assign(draft, posts);
    });
}

// Helper atom to get a single post by ID (read-only)
export const getPostByIdAtom = atom(get => (postId: string | undefined): Post | undefined => {
    if (!postId) return undefined;
    const posts = get(postsAtom);
    return posts[postId];
});
getPostByIdAtom.debugLabel = 'getPostByIdAtom';

// Helper to find a post by identifier (slug or ID) - reads the current state
export const findPostByIdentifier = (identifier: string): Post | undefined => {
    const posts = jotaiStore.get(postsAtom);
    for (const post of Object.values(posts)) {
        if (post.id === identifier || post.slugUrl === identifier) return post;
    }
    return undefined;
};

// --- Derived Atoms for Reading Post Data ---

export const postAtomFamily = atomFamily((postId: string | undefined) => {
    const derivedAtom = atom<Post | undefined>(get => (postId ? get(postsAtom)[postId] : undefined));
    derivedAtom.debugLabel = `postAtomFamily/${postId ?? 'undefined'}`;
    return derivedAtom;
});

export const loadPostByIdentifierAtom = atom(null, async (get, set, identifier: string) => {
    const post = await fetchPostBySlugOrId(identifier);

    if (!post) throw new Error(`Post not found: ${identifier}`);

    // Update the main post cache
    setPost(post);
    return post;
});

// ----- Post Collection State Management -----
// (Keeping this section largely the same as it seemed well-structured)

export interface PostCollectionCompositeKeyParams {
    type: PostCollectionType;
    tokenName: string;
    flairId?: string;
    userId?: string;
}

export function generatePostCollectionKey(params: PostCollectionCompositeKeyParams): string {
    let key = `${params.type}_${params.tokenName}`;
    if (params.flairId) key += `_flair-${params.flairId}`;
    if (params.userId) key += `_user-${params.userId}`;
    return key;
}

interface PostCollectionState {
    ids: string[];
    lastDoc: QueryDocumentSnapshot<Post> | null;
    hasMore: boolean;
    status: LoadStatus;
    error: Error | null;
}

/** Atom family managing state for lists/collections of posts */
export const postCollectionStateFamily = atomFamily(
    (params: PostCollectionCompositeKeyParams) => {
        const key = generatePostCollectionKey(params);
        const baseAtom = atomWithImmer<PostCollectionState>({
            ids: [],
            lastDoc: null,
            hasMore: true,
            status: 'idle',
            error: null,
        });
        baseAtom.debugLabel = `postCollectionState_${key}`;
        return baseAtom;
        // Use custom equality check because params is an object
    },
    (a, b) => generatePostCollectionKey(a) === generatePostCollectionKey(b),
);
postCollectionStateFamily.debugLabel = 'postCollectionStateFamily';

const getQueryConstraints = (params: PostCollectionCompositeKeyParams): QueryConstraint[] => {
    const { type, tokenName, flairId, userId } = params;
    const constraints: QueryConstraint[] = [where('tokenName', '==', tokenName)];

    switch (type) {
        case 'partner':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('createdByWhitelistedUser', '==', true), orderBy('createdAt', 'desc'));
            if (flairId) constraints.push(where('flairs', 'array-contains', getFlairNameById(flairId, tokenName)));
            break;
        case 'community':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('createdByWhitelistedUser', '==', false), orderBy('createdAt', 'desc'));
            if (flairId) constraints.push(where('flairs', 'array-contains', getFlairNameById(flairId, tokenName)));
            break;
        case 'pinned':
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('isPinned', '==', true), orderBy('pinnedTime', 'desc'));
            break;
        case 'recommended': {
            const lastWeek = new Date();
            lastWeek.setDate(lastWeek.getDate() - 7);
            constraints.push(where('status', '==', PostStatus.PUBLISHED), where('createdAt', '>=', lastWeek), orderBy('totalViews', 'desc'));
            break;
        }
        case 'user':
            if (!userId) throw new Error('userId is required for user post collection');
            constraints.push(where('publisherId', '==', userId), where('status', '==', PostStatus.PUBLISHED), orderBy('createdAt', 'desc'));
            break;
        case 'pending':
            constraints.push(where('status', 'in', [PostStatus.PENDING]), orderBy('createdAt', 'desc'));
            break;
        case 'scheduled':
            constraints.push(where('status', 'in', [PostStatus.PUBLISHED_SCHEDULED]), orderBy('scheduledAt', 'asc'));
            break;
        case 'draft':
            constraints.push(where('status', 'in', [PostStatus.DRAFT]), orderBy('createdAt', 'desc'));
            break;
        case 'deleted': {
            const deleteStatuses = [PostStatus.DELETED_BY_DOMAIN_ADMIN, PostStatus.DELETED_BY_CATEGORY_ADMIN, PostStatus.DELETED_BY_TOKEN_ADMIN];
            constraints.push(where('status', 'in', deleteStatuses), orderBy('createdAt', 'desc'));
            break;
        }
        default:
            ((_: never) => {
                throw new Error(`Unsupported post type: ${type}`);
            })(type);
    }
    return constraints;
};

export type LoadMorePostsParams = PostCollectionCompositeKeyParams & { fetchLimit?: number };

/** Write-only atom to trigger loading more posts for a collection */
export const loadMorePostsAtom = atom(null, async (get, set, params: LoadMorePostsParams) => {
    const { fetchLimit = 20, ...keyParams } = params;
    const collectionAtom = postCollectionStateFamily(keyParams);
    const collectionKey = generatePostCollectionKey(keyParams);
    const currentState = get(collectionAtom);

    if (currentState.status === 'loading' || !currentState.hasMore) return;

    set(collectionAtom, draft => {
        draft.status = 'loading';
        draft.error = null;
    });

    try {
        const queryConstraints = getQueryConstraints(keyParams);
        const lowerBound = currentState.lastDoc;
        const { ids, postsData, lastDoc, hasMore } = await fetchPosts({ fetchLimit, queryConstraints, lowerBound });

        // Update the global post cache
        setPosts(postsData);

        // Update the specific collection's state
        set(collectionAtom, draft => {
            // Add only new IDs to preserve order if necessary, or simply merge
            const existingIds = new Set(draft.ids);
            ids.forEach(id => existingIds.add(id)); // Simple merge strategy
            // If order matters and you fetch pages, you might append: draft.ids.push(...ids.filter(id => !existingIds.has(id)));
            draft.ids = Array.from(existingIds); // Or the ordered appended list
            draft.lastDoc = lastDoc ?? null;
            draft.hasMore = hasMore;
            draft.status = 'success';
            draft.error = null;
        });
    } catch (error) {
        console.error(`[loadMorePostsAtom ${collectionKey}] Error:`, error);
        set(collectionAtom, draft => {
            draft.status = 'error';
            draft.error = error instanceof Error ? error : new Error(String(error) || 'Unknown error');
        });
    }
});
loadMorePostsAtom.debugLabel = 'loadMorePostsAtom';

// ----- Data Fetching Functions -----
// (Assume these interact correctly with Firebase)

interface FetchPostsPayload {
    lowerBound?: DocumentSnapshot<Post> | null;
    fetchLimit?: number;
    queryConstraints?: QueryConstraint[];
}

/** Fetches a batch of posts based on constraints */
async function fetchPosts(payload: FetchPostsPayload): Promise<{ ids: string[]; postsData: Record<string, Post>; lastDoc: QueryDocumentSnapshot<Post> | null; hasMore: boolean }> {
    const { lowerBound, fetchLimit = 20, queryConstraints = [] } = payload;
    const db = getFirestore(creatorSdk.getApp());
    const postsRef = collection(db, 'Post') as CollectionReference<Post>;
    const paginationConstraints = lowerBound ? [startAfter(lowerBound)] : [];

    const postsQuery = query(postsRef, ...queryConstraints, ...paginationConstraints, limit(fetchLimit));
    const snapshot = await getDocs(postsQuery);

    const ids: string[] = [];
    const postsData: Record<string, Post> = {};
    snapshot.forEach(docSnap => {
        const data = docSnap.data();
        // Ensure ID is part of the post object
        postsData[docSnap.id] = { ...data, id: docSnap.id };
        ids.push(docSnap.id);
    });

    const lastDoc = (snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<Post>) ?? null;
    const hasMore = snapshot.size === fetchLimit;

    return { ids, postsData, lastDoc, hasMore };
}

/** Fetches a single post by its ID */
async function fetchPost(postId: string): Promise<Post | undefined> {
    const db = getFirestore(creatorSdk.getApp());
    const postRef = doc(db, 'Post', postId) as DocumentReference<Post>;
    const snapshot = await getDoc(postRef);
    if (!snapshot.exists()) return undefined;
    // Ensure ID is part of the post object
    return { ...snapshot.data(), id: snapshot.id };
}

/** Fetches a single post by its slugUrl */
async function fetchPostBySlugUrl(slugUrl: string): Promise<Post | undefined> {
    // Use the generic fetchPosts which handles ID injection
    const { ids, postsData } = await fetchPosts({ fetchLimit: 1, queryConstraints: [where('slugUrl', '==', slugUrl)] });
    return ids[0] ? postsData[ids[0]] : undefined;
}

/** Fetches a single post by trying slugUrl first, then falling back to ID */
async function fetchPostBySlugOrId(postSlugOrId: string): Promise<Post | undefined> {
    // Simple check: Assume it's a slug if it doesn't match typical ID patterns (e.g., length, characters)
    // This is heuristic; adjust if your slugs and IDs can overlap significantly.
    const looksLikeId = postSlugOrId.length === 20 && /^[a-zA-Z0-9]+$/.test(postSlugOrId); // Example heuristic

    let post: Post | undefined;
    if (!looksLikeId) post = await fetchPostBySlugUrl(postSlugOrId);

    // If not found by slug (or if it looked like an ID initially), try fetching by ID
    if (!post) post = await fetchPost(postSlugOrId);

    if (!post) console.warn(`Post not found by slug or ID: ${postSlugOrId}`);

    return post;
}
